import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RequireWrite, TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import {
  deleteFund,
  getFund,
  updateFund,
  type FundStatus,
  type FundStrategy,
  type FundStructure,
} from "@/lib/api/funds";

const STRUCTURES: FundStructure[] = [
  "limited_partnership",
  "llc",
  "trust",
  "plc",
  "sicav",
  "other",
];
const STRATEGIES: FundStrategy[] = [
  "buyout",
  "growth",
  "venture",
  "credit",
  "real_estate",
  "infrastructure",
  "fund_of_funds",
  "other",
];
const STATUSES: FundStatus[] = ["forming", "fundraising", "active", "harvesting", "wound_down"];

function FundsEditInner() {
  const tenant = useTenant();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { fundId } = useParams({ strict: false });

  const {
    data: fund,
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey: ["fund", fundId],
    queryFn: () => {
      if (!fundId) throw new Error("fundId param missing");
      return getFund(fundId);
    },
    enabled: !!fundId,
  });

  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [vintage, setVintage] = useState("");
  const [sizeUsd, setSizeUsd] = useState("");
  const [domicile, setDomicile] = useState("");
  const [structure, setStructure] = useState<FundStructure | "">("");
  const [strategy, setStrategy] = useState<FundStrategy | "">("");
  const [status, setStatus] = useState<FundStatus>("forming");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!fund) return;
    setName(fund.name);
    setShortName(fund.short_name ?? "");
    setVintage(fund.vintage_year?.toString() ?? "");
    setSizeUsd(fund.fund_size_usd?.toString() ?? "");
    setDomicile(fund.domicile ?? "");
    setStructure(fund.structure ?? "");
    setStrategy(fund.strategy ?? "");
    setStatus(fund.status);
  }, [fund]);

  const update = useMutation({
    mutationFn: (id: string) =>
      updateFund(id, {
        name,
        short_name: shortName || null,
        vintage_year: vintage ? Number(vintage) : null,
        fund_size_usd: sizeUsd ? Number(sizeUsd) : null,
        domicile: domicile || null,
        structure: structure || null,
        strategy: strategy || null,
        status,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["funds", tenant.id] });
      await qc.invalidateQueries({ queryKey: ["fund", fundId] });
      await navigate({ to: "/t/$slug/funds", params: { slug: tenant.slug } });
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteFund(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["funds", tenant.id] });
      await navigate({ to: "/t/$slug/funds", params: { slug: tenant.slug } });
    },
    onError: (err: Error) => setFormError(err.message),
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!fundId) return;
    setFormError(null);
    update.mutate(fundId);
  }

  function onDelete(): void {
    if (!fundId || !fund) return;
    const ok = window.confirm(
      `Delete ${fund.name}? This also deletes its LP commitments. This cannot be undone.`,
    );
    if (!ok) return;
    setFormError(null);
    del.mutate(fundId);
  }

  if (isLoading) {
    return <p className="text-sm text-neutral-500">Loading fund…</p>;
  }
  if (loadError || !fund) {
    return (
      <div className="space-y-3">
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {loadError ? loadError.message : "Fund not found"}
        </div>
        <Link
          to="/t/$slug/funds"
          params={{ slug: tenant.slug }}
          className="inline-block text-xs text-neutral-400 hover:text-neutral-200"
        >
          ← Back to funds
        </Link>
      </div>
    );
  }

  return (
    <>
      <header className="space-y-1">
        <Link
          to="/t/$slug/funds"
          params={{ slug: tenant.slug }}
          className="text-xs text-neutral-500 hover:text-neutral-300"
        >
          ← Back to funds
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">Edit {fund.name}</h1>
      </header>

      <form onSubmit={onSubmit} className="max-w-2xl space-y-4">
        <Field label="Name" required>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Short name">
            <input
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Vintage year">
            <input
              type="number"
              min={1900}
              max={2200}
              value={vintage}
              onChange={(e) => setVintage(e.target.value)}
              className="input"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Fund size (USD)">
            <input
              type="number"
              min={0}
              step={1000000}
              value={sizeUsd}
              onChange={(e) => setSizeUsd(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Domicile">
            <input
              value={domicile}
              onChange={(e) => setDomicile(e.target.value)}
              className="input"
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Structure">
            <select
              value={structure}
              onChange={(e) => setStructure(e.target.value as FundStructure | "")}
              className="input"
            >
              <option value="">—</option>
              {STRUCTURES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Strategy">
            <select
              value={strategy}
              onChange={(e) => setStrategy(e.target.value as FundStrategy | "")}
              className="input"
            >
              <option value="">—</option>
              {STRATEGIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as FundStatus)}
              className="input"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {formError ? (
          <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
            {formError}
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={update.isPending}
              className="rounded bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-950 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {update.isPending ? "Saving…" : "Save changes"}
            </button>
            <Link
              to="/t/$slug/funds"
              params={{ slug: tenant.slug }}
              className="rounded border border-neutral-800 px-4 py-2 text-sm text-neutral-300 hover:bg-neutral-900"
            >
              Cancel
            </Link>
          </div>
          <button
            type="button"
            onClick={onDelete}
            disabled={del.isPending}
            className="rounded border border-red-900/60 px-4 py-2 text-sm text-red-300 hover:bg-red-950/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {del.isPending ? "Deleting…" : "Delete fund"}
          </button>
        </div>
      </form>
    </>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}

function Field({ label, required, children }: FieldProps) {
  return (
    <label className="block space-y-1">
      <span className="text-xs uppercase tracking-wide text-neutral-400">
        {label}
        {required ? <span className="ml-0.5 text-red-400">*</span> : null}
      </span>
      {children}
    </label>
  );
}

export function FundsEditPage() {
  return (
    <TenantLayout>
      <RequireWrite>
        <FundsEditInner />
      </RequireWrite>
    </TenantLayout>
  );
}
